import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import api from '../../services/api';

const INK = '#1f242e';
const SLATE = [63, 70, 82];
const HAIR = [208, 212, 218];
const CYAN = [101, 198, 224];
const MUTED = '#6a7180';

const ENTITY_LABELS = {
  event: 'EVENTO',
  task: 'TAREA',
  armado: 'LOGÍSTICA',
  operation: 'LOGÍSTICA',
};

const S = value => String(value ?? '').trim();

function formatDate(value) {
  const date = S(value).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return '';
  const [year, month, day] = date.split('-');
  return `${day}/${month}/${year}`;
}

function formatTime(value) {
  const text = S(value).replace('.', ':');
  if (/^\d{1,2}$/.test(text)) return `${text.padStart(2, '0')}:00`;
  return /^\d{1,2}:\d{2}/.test(text) ? text.slice(0, 5).padStart(5, '0') : '';
}

function formatSchedule(phase) {
  const date = formatDate(phase.date);
  const start = formatTime(phase.start);
  return [date, start].filter(Boolean).join(' | ');
}

function clean(value) {
  return S(value).replace(/[\r\n]+/g, ' ').replace(/\s{2,}/g, ' ');
}

function filename(data) {
  const date = data.phases.find(phase => phase.people.length)?.date || '';
  const parts = ['Lista de personal', data.title, formatDate(date).replace(/\//g, '.'), 'LANG'].filter(Boolean);
  return `${parts.join(' - ').replace(/[/\\:*?"<>|]/g, '·')}.pdf`;
}

function loadLogo(src) {
  return new Promise(resolve => {
    if (typeof Image === 'undefined') return resolve(null);
    const image = new Image();
    image.onload = () => {
      try {
        const targetHeight = Math.min(240, image.naturalHeight);
        const scale = targetHeight / image.naturalHeight;
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(image.naturalWidth * scale);
        canvas.height = targetHeight;
        canvas.getContext('2d').drawImage(image, 0, 0, canvas.width, canvas.height);
        resolve({ dataUrl: canvas.toDataURL('image/png'), width: canvas.width, height: canvas.height });
      } catch {
        resolve(null);
      }
    };
    image.onerror = () => resolve(null);
    image.src = src;
  });
}

function eventReference(data) {
  if (!data.linked_event) return '';
  return [data.linked_event.code, data.linked_event.name].filter(Boolean).join(' · ');
}

// Genera el jsPDF de la lista de personal. Devuelve { doc, data }, o null si no
// hay personal asignado (avisa). Lanza si la generación falla; cada entrada
// (exportar/construir) maneja el catch.
async function generatePersonnelDoc({ entityType, entityId }) {
  const { data } = await api.get('/staff-assignments/personnel-pdf', {
    params: { [`${entityType}_id`]: entityId },
  });
  const populatedPhases = (data.phases || []).filter(phase => phase.people?.length);
  if (!populatedPhases.length) {
    window.alert('No hay personal asignado para exportar.');
    return null;
  }

  {
    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 52;
    const logo = await loadLogo('/logo2.png');

    doc.setDrawColor(...HAIR).setLineWidth(0.8);
    doc.line(0, 30, pageWidth, 30);
    if (logo) {
      const height = 40;
      doc.addImage(logo.dataUrl, 'PNG', margin, 50, (logo.width / logo.height) * height, height);
    } else {
      doc.setFont('helvetica', 'bold').setFontSize(28).setTextColor(INK);
      doc.text('LANG', margin, 80);
    }

    const today = new Date();
    const issued = `${String(today.getDate()).padStart(2, '0')}/${String(today.getMonth() + 1).padStart(2, '0')}/${today.getFullYear()}`;
    doc.setFont('helvetica', 'normal').setFontSize(8.5).setTextColor(MUTED);
    doc.text('L I S T A   D E   P E R S O N A L', pageWidth - margin, 58, { align: 'right' });
    doc.text(`Emitido: ${issued}`, pageWidth - margin, 74, { align: 'right' });

    let y = 122;
    const meta = [
      { text: `${ENTITY_LABELS[data.entity_type] || 'REGISTRO'}: ${clean([data.code, data.title].filter(Boolean).join(' · ')).toUpperCase()}`, bold: true },
      data.client ? { text: `CLIENTE: ${clean(data.client).toUpperCase()}`, bold: false } : null,
      data.place ? { text: `LUGAR: ${clean(data.place).toUpperCase()}`, bold: true } : null,
      data.sales_referent ? { text: `REFERENTE: ${clean(data.sales_referent).toUpperCase()}`, bold: true } : null,
      eventReference(data) ? { text: `EVENTO VINCULADO: ${clean(eventReference(data)).toUpperCase()}`, bold: false } : null,
    ].filter(Boolean);

    doc.setFontSize(10.5).setTextColor(INK);
    meta.forEach((item) => {
      doc.setFont('helvetica', item.bold ? 'bold' : 'normal');
      doc.splitTextToSize(item.text, pageWidth - margin * 2).forEach(part => {
        doc.text(part, margin, y);
        y += 16;
      });
    });
    y += 6;
    doc.setFont('helvetica', 'normal').setFontSize(9.5).setTextColor(MUTED);
    doc.text('Personal asignado por fase operativa.', margin, y);
    y += 14;

    populatedPhases.forEach((phase, index) => {
      if (index > 0) y += 26;
      doc.setFont('helvetica', 'bold').setFontSize(10).setTextColor(INK);
      doc.text(`${phase.label.toUpperCase()}${formatSchedule(phase) ? ` · ${formatSchedule(phase)}` : ''}`, margin, y);
      y += 8;
      autoTable(doc, {
        startY: y,
        margin: { left: margin, right: margin },
        head: [['#', 'Nombre', 'Apellido', 'Cédula']],
        body: phase.people.map((person, rowIndex) => [
          String(rowIndex + 1),
          person.first_name || '',
          person.last_name || '',
          person.cedula || '',
        ]),
        theme: 'plain',
        styles: {
          font: 'helvetica', fontSize: 9.5, textColor: INK,
          cellPadding: { top: 7, bottom: 7, left: 8, right: 8 }, valign: 'middle',
        },
        headStyles: {
          fillColor: SLATE, textColor: 255, fontStyle: 'bold', fontSize: 9,
          cellPadding: { top: 7, bottom: 7, left: 8, right: 8 },
        },
        columnStyles: {
          0: { cellWidth: 34, halign: 'center' },
          1: { cellWidth: 'auto' },
          2: { cellWidth: 155 },
          3: { cellWidth: 110 },
        },
        didDrawCell: table => {
          if (table.section === 'body' && table.column.index === 3) {
            doc.setDrawColor(...HAIR).setLineWidth(0.5);
            doc.line(margin, table.cell.y + table.cell.height, pageWidth - margin, table.cell.y + table.cell.height);
          }
        },
      });
      y = doc.lastAutoTable.finalY;
    });

    const totalPeople = populatedPhases.reduce((total, phase) => total + phase.people.length, 0);
    const footY = pageHeight - 46;
    doc.setDrawColor(...CYAN).setLineWidth(1.4);
    doc.line(margin, footY, pageWidth - margin, footY);
    doc.setFont('helvetica', 'normal').setFontSize(8.5).setTextColor(MUTED);
    doc.text('LANG · lang.uy', margin, footY + 16);
    doc.text(`${totalPeople} persona${totalPeople === 1 ? '' : 's'}`, pageWidth - margin, footY + 16, { align: 'right' });
    return { doc, data };
  }
}

// Escritorio: genera y baja el PDF. En PC el <a download> de doc.save() funciona.
export async function exportPersonnelPdf({ entityType, entityId }) {
  try {
    const result = await generatePersonnelDoc({ entityType, entityId });
    if (result) result.doc.save(filename(result.data));
  } catch (error) {
    window.alert(error?.response?.data?.error || 'No se pudo generar el PDF de personal.');
  }
}

// Celular (paso 1): genera el PDF y devuelve el blob listo. La entrega se hace
// aparte (deliverPersonnelPdf) en un gesto fresco: iOS exige que la hoja de
// compartir salga de un toque, y acá ya se gastó en el fetch.
export async function buildPersonnelPdf({ entityType, entityId }) {
  try {
    const result = await generatePersonnelDoc({ entityType, entityId });
    if (!result) return null;
    return { blob: result.doc.output('blob'), filename: filename(result.data) };
  } catch (error) {
    window.alert(error?.response?.data?.error || 'No se pudo generar el PDF de personal.');
    return null;
  }
}

// Celular (paso 2): entrega el PDF en un gesto fresco. En iPhone/Android abre la
// hoja de compartir nativa (guardar en Archivos, mandar por WhatsApp/mail); si no
// está disponible, intenta abrir/bajar el blob.
export async function deliverPersonnelPdf({ blob, filename: name }) {
  const file = typeof File !== 'undefined' ? new File([blob], name, { type: 'application/pdf' }) : null;
  if (file && navigator.canShare?.({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: name });
      return;
    } catch (error) {
      if (error?.name === 'AbortError') return; // el usuario cerró la hoja
      // otro error (p. ej. no permitido): caemos al fallback de abrir/bajar
    }
  }
  // Sin hoja de compartir (iOS viejo, o share que falló). window.open de un blob
  // suele bloquearse en iOS y WebKit ignora `download`; navegar la pestaña actual
  // al blob SÍ abre el visor de PDF integrado. En desktop abre una pestaña nueva.
  const url = URL.createObjectURL(blob);
  if (!window.open(url, '_blank')) {
    window.location.href = url;
  }
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}
